import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentMember, isAuthenticated, getAccountHealth, getAppConfig, Member, AccountHealth, AppConfig } from '../services/api';
import { Trophy, Medal, Award, TrendingUp, Gift, Ticket, Calendar, Sparkles, HelpCircle, ChevronDown, ChevronUp, ShoppingBag, Users, Star, CreditCard, ShieldAlert, Share2, Copy, Check } from 'lucide-react';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [member, setMember] = useState<Member | null>(null);
  const [health, setHealth] = useState<AccountHealth | null>(null);
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEarnInfo, setShowEarnInfo] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadMemberData();
  }, []);

  const loadMemberData = async () => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      const data = await getCurrentMember();
      setMember(data);
      // Load health and config in background (non-blocking)
      getAccountHealth().then(setHealth).catch(() => {});
      getAppConfig().then(setAppConfig).catch(() => {});
    } catch (err) {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'Gold': return Trophy;
      case 'Silver': return Medal;
      default: return Award;
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'Gold': return 'text-yellow-500';
      case 'Silver': return 'text-gray-400';
      default: return 'text-amber-700';
    }
  };

  const getTierBgColor = (tier: string) => {
    switch (tier) {
      case 'Gold': return 'bg-yellow-500';
      case 'Silver': return 'bg-gray-400';
      default: return 'bg-amber-700';
    }
  };

  const copyReferralCode = async () => {
    if (!member?.referralCode) return;

    const referralUrl = `https://app.insiderpass.io/signup?ref=${member.referralCode}`;
    const shareText = `Join me on InsiderPass at BuggyBusters and get exclusive rewards! Sign up here: ${referralUrl}`;

    try {
      // Try native share first (mobile)
      // Only pass text (which includes the URL) - don't pass url separately
      // as some apps only show the url and ignore the text
      if (navigator.share) {
        await navigator.share({
          text: shareText,
        });
      } else {
        // Fallback to clipboard - copy the full message with URL
        await navigator.clipboard.writeText(shareText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      // Fallback to clipboard if share cancelled
      try {
        await navigator.clipboard.writeText(shareText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        console.error('Failed to copy');
      }
    }
  };

  const getNextTier = (current: string) => {
    if (current === 'Bronze') return { tier: 'Silver', points: 300 };
    if (current === 'Silver') return { tier: 'Gold', points: 600 };
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-buggy-blue mx-auto mb-4"></div>
          <p className="text-buggy-gray">Loading...</p>
        </div>
      </div>
    );
  }

  if (!member) return null;

  const nextTier = getNextTier(member.currentTier);
  const pointsToNext = nextTier ? nextTier.points - member.currentPoints : 0;
  const progress = nextTier ? (member.currentPoints / nextTier.points) * 100 : 100;
  const TierIcon = getTierIcon(member.currentTier);
  const tierColor = getTierColor(member.currentTier);
  const tierBgColor = getTierBgColor(member.currentTier);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40">
        <div className="bg-buggy-blue text-white p-6">
          <div className="max-w-md mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">Welcome back, {member.firstName}!</h1>
                <p className="text-sm opacity-90">Your exclusive rewards await</p>
              </div>
              <div className="flex flex-col items-end">
                <div className="inline-flex items-center gap-1.5 bg-white rounded-full px-3 py-1.5 mb-2 shadow-sm">
                  <TierIcon size={16} strokeWidth={2.5} className={tierColor} />
                  <span className="text-xs font-semibold text-buggy-dark">{member.currentTier} Tier</span>
                </div>
                <p className="text-xs opacity-75">Your Points</p>
                <p className="text-2xl font-bold">{member.currentPoints}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto pb-20">
        <div className="max-w-md mx-auto p-4 space-y-4">
        
        {/* Progress Card */}
        <div className="bg-white rounded-xl shadow-lg p-5">
          {nextTier ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-buggy-dark">Progress to {nextTier.tier}</span>
                <span className="text-sm text-buggy-gray">{member.currentPoints} / {nextTier.points} pts</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
                <div 
                  className={`${tierBgColor} h-3 rounded-full transition-all duration-500`}
                  style={{ width: `${Math.min(progress, 100)}%` }}
                ></div>
              </div>
              <p className="text-xs text-buggy-gray text-center">
                <span className="font-semibold text-buggy-blue">{pointsToNext} more points</span> to unlock {nextTier.tier} perks
              </p>
            </>
          ) : (
            <div className="text-center py-2">
              <Trophy size={32} strokeWidth={2} className="text-yellow-500 mx-auto mb-2" />
              <p className="font-semibold text-buggy-dark">You've reached the highest tier!</p>
              <p className="text-sm text-buggy-gray mt-1">Enjoy all exclusive Gold benefits</p>
            </div>
          )}

          {/* How to Earn Points - Expandable */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={() => setShowEarnInfo(!showEarnInfo)}
              className="w-full flex items-center justify-between text-sm text-buggy-blue hover:text-buggy-blue-light"
            >
              <div className="flex items-center gap-2">
                <HelpCircle size={16} strokeWidth={2} />
                <span className="font-medium">How to earn points</span>
              </div>
              {showEarnInfo ? (
                <ChevronUp size={18} strokeWidth={2} />
              ) : (
                <ChevronDown size={18} strokeWidth={2} />
              )}
            </button>

            {showEarnInfo && (
              <div className="mt-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-buggy-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ShoppingBag size={16} strokeWidth={2} className="text-buggy-blue" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-buggy-dark">Shop In-Store</p>
                    <p className="text-xs text-buggy-gray">Earn 1 point for every $1 spent</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-buggy-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Gift size={16} strokeWidth={2} className="text-buggy-blue" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-buggy-dark">Auction Method</p>
                    <p className="text-xs text-buggy-gray">Earn 2 points for every $1 at auctions</p>
                  </div>
                </div>
                {(appConfig?.showReferralProgram !== false) && (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-buggy-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Users size={16} strokeWidth={2} className="text-buggy-blue" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-buggy-dark">Refer a Friend</p>
                      <p className="text-xs text-buggy-gray">Get {appConfig?.referralBonusPoints || 50} bonus points per referral</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-buggy-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Star size={16} strokeWidth={2} className="text-buggy-blue" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-buggy-dark">Birthday Bonus</p>
                    <p className="text-xs text-buggy-gray">Earn 25 bonus points on your birthday</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Account Health Banner — only shown when health is degraded */}
        {health && health.status !== 'Excellent' && health.status !== 'Good' && (
          <button
            onClick={() => navigate('/health')}
            className={`w-full rounded-xl p-4 flex items-center gap-3 text-left ${
              health.isBanned || health.isSuspended
                ? 'bg-red-600 text-white'
                : health.status === 'Critical' || health.status === 'Poor'
                ? 'bg-orange-100 border border-orange-300'
                : 'bg-yellow-50 border border-yellow-300'
            }`}
          >
            <ShieldAlert size={24} strokeWidth={2} className={health.isBanned || health.isSuspended ? 'text-white flex-shrink-0' : 'text-orange-600 flex-shrink-0'} />
            <div className="flex-1">
              <p className={`font-bold text-sm ${health.isBanned || health.isSuspended ? 'text-white' : 'text-buggy-dark'}`}>
                {health.isBanned ? 'Account Permanently Suspended' : health.isSuspended ? 'Account Temporarily Suspended' : `Account Health: ${health.status}`}
              </p>
              <p className={`text-xs mt-0.5 ${health.isBanned || health.isSuspended ? 'text-red-100' : 'text-gray-600'}`}>
                Tap to view your policy standing and violations
              </p>
            </div>
          </button>
        )}

        {/* My Card Quick Access */}
        <button
          onClick={() => navigate('/card')}
          className="w-full bg-gradient-to-r from-buggy-blue to-blue-600 rounded-xl shadow-lg p-5 hover:shadow-xl transition-shadow text-left flex items-center gap-4"
        >
          <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <CreditCard size={28} strokeWidth={2} className="text-white" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-white text-lg">My Card</p>
            <p className="text-sm text-white/80">Show QR code at checkout to earn points</p>
          </div>
        </button>

        {/* Share & Earn Referral Section */}
        {member.referralCode && (appConfig?.showReferralProgram !== false) && (
          <div className="bg-gradient-to-r from-purple-600 to-pink-500 rounded-xl shadow-lg p-5 text-white">
            <div className="flex items-center gap-2 mb-3">
              <Share2 size={20} strokeWidth={2} />
              <h3 className="font-bold">Share & Earn {appConfig?.referralBonusPoints || 50} Points</h3>
            </div>
            <p className="text-sm text-white/90 mb-4">
              Invite friends to join InsiderPass. When they sign up with your code, you earn {appConfig?.referralBonusPoints || 50} bonus points!
            </p>
            <div className="bg-white/20 rounded-lg p-3 flex items-center justify-between mb-3">
              <div>
                <p className="text-xs text-white/70">Your referral code</p>
                <p className="text-xl font-mono font-bold tracking-wider">{member.referralCode}</p>
              </div>
              <button
                onClick={copyReferralCode}
                className="bg-white text-purple-600 px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 hover:bg-purple-50 transition-colors"
              >
                {copied ? (
                  <>
                    <Check size={16} strokeWidth={2.5} />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={16} strokeWidth={2} />
                    Share
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-white/70 text-center">
              Friends enter your code when signing up at app.insiderpass.io
            </p>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/rewards')}
            className="bg-white rounded-xl shadow-md p-5 hover:shadow-lg transition-shadow text-left"
          >
            <div className="w-12 h-12 bg-buggy-blue/10 rounded-lg flex items-center justify-center mb-3">
              <Gift size={24} strokeWidth={2} className="text-buggy-blue" />
            </div>
            <p className="font-bold text-buggy-dark">Browse Rewards</p>
            <p className="text-xs text-buggy-gray mt-1">Explore exclusive perks</p>
          </button>

          <button
            onClick={() => navigate('/redemptions')}
            className="bg-white rounded-xl shadow-md p-5 hover:shadow-lg transition-shadow text-left"
          >
            <div className="w-12 h-12 bg-buggy-blue/10 rounded-lg flex items-center justify-center mb-3">
              <Ticket size={24} strokeWidth={2} className="text-buggy-blue" />
            </div>
            <p className="font-bold text-buggy-dark">My Redemptions</p>
            <p className="text-xs text-buggy-gray mt-1">View your codes</p>
          </button>
        </div>

        {/* Tier Benefits */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={20} strokeWidth={2} className="text-buggy-blue" />
            <h3 className="font-bold text-buggy-dark">Your {member.currentTier} Benefits</h3>
          </div>

          <div className="space-y-3">
            {member.currentTier === 'Bronze' && (
              <>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Gift size={20} strokeWidth={2} className="text-amber-700" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-buggy-dark">Birthday Bonus</p>
                    <p className="text-xs text-buggy-gray">Extra 10% off during your birthday month</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Calendar size={20} strokeWidth={2} className="text-amber-700" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-buggy-dark">Extended Returns</p>
                    <p className="text-xs text-buggy-gray">30-day return window</p>
                  </div>
                </div>
              </>
            )}

            {member.currentTier === 'Silver' && (
              <>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                    <Award size={20} strokeWidth={2} className="text-gray-500" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-buggy-dark">All Bronze Benefits</p>
                    <p className="text-xs text-buggy-gray">Plus exclusive Silver perks</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                    <Calendar size={20} strokeWidth={2} className="text-gray-500" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-buggy-dark">Early Shopping Hours</p>
                    <p className="text-xs text-buggy-gray">Shop before opening on Tuesdays</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                    <TrendingUp size={20} strokeWidth={2} className="text-gray-500" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-buggy-dark">Reserved VIP Parking</p>
                    <p className="text-xs text-buggy-gray">VIP parking spot near entrance</p>
                  </div>
                </div>
              </>
            )}

            {member.currentTier === 'Gold' && (
              <>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Medal size={20} strokeWidth={2} className="text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-buggy-dark">All Silver Benefits</p>
                    <p className="text-xs text-buggy-gray">Plus exclusive Gold perks</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Gift size={20} strokeWidth={2} className="text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-buggy-dark">Warehouse Tours</p>
                    <p className="text-xs text-buggy-gray">Behind-the-scenes access</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <TrendingUp size={20} strokeWidth={2} className="text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-buggy-dark">Personal Shopper</p>
                    <p className="text-xs text-buggy-gray">1-hour dedicated assistance</p>
                  </div>
                </div>
              </>
            )}
          </div>

          <button
            onClick={() => navigate('/rewards')}
            className="w-full mt-4 text-center text-sm text-buggy-blue font-semibold hover:text-buggy-blue-light"
          >
            View all rewards →
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
